/**
 * Easter coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterColoringPagesPack: ToilePack = {
    slug: 'easter-coloring-pages',
    category: 'season',
    name: 'Easter coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bunny', label: 'Bunny basket', subject: 'A bunny holding a basket overflowing with eggs, tulips around, a butterfly'},
        {key: 'egg', label: 'Patterned egg', subject: 'A single huge egg filling the page divided into bands of patterns: zigzags, dots, flowers, stripes'},
        {key: 'chick', label: 'Chick in a nest', subject: 'A chick in a nest of straw with two eggs, daisies and a ladybird around it'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a fox instead', 'in a spring garden', 'as a two-page spread'],
    directionHint: 'try: a fox instead',
    seo: {
        title: 'Easter coloring pages: bunny, egg, chick',
        metaDescription: 'Nine free Easter coloring pages — a bunny with a basket, a patterned egg, a chick in a nest — in simple, detailed or doodle line art.',
        h1: 'Easter coloring pages: bunny, patterned egg and chick',
        tagline: 'Nine coloring pages, three Easter scenes, three line styles each — clean outlines ready to fill in.',
        intro: 'An Easter coloring page for spring: a bunny holding a basket of eggs among tulips, one huge patterned egg banded in zigzags and flowers, a chick in a straw nest with daisies around it. Each is drawn in bold simple lines, intricate detailed line art, or loose hand-drawn doodle strokes. Flat black outlines, no shading, ready to color. Ask for a different scene, like a fox, and it redraws.',
        useCases: [
            'a free Easter coloring page for kids',
            'a classroom spring activity',
            'an Easter basket table activity',
            'a rainy-day printable for the holiday',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each page is drawn at portrait A4 proportions with a white margin, so it prints clean on standard paper.'},
            {q: 'Are these good for young kids?', a: 'The simple style uses large open shapes and thick lines suited to smaller hands; detailed and doodle suit older colorists who want more to fill in.'},
            {q: 'Can I get a different Easter scene?', a: "Yes — that's what the direction field is for. Type a different scene, like a fox or a spring garden, and it redraws in the same three line styles."},
        ],
    },
}
